import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService, AiAgentActivity } from '../../services/api';
import { AiBotIcon } from '../icons/AppIcon';
import { Screen } from '../../contexts/AppContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { colors, spacing, typography, withAlpha } from '../../theme';
import { designTokens } from '../../theme/design-system';

/**
 * Home → AI activity: for the picked range, what each AI agent did — conversations, replies,
 * orders and quotations (with value), handoffs to people, and runs with errors. Tapping an agent
 * opens its runs. Hidden when the business has no AI agents.
 */

const money = (amount: number, currency: string) => `${Math.round(amount).toLocaleString()}${currency ? ` ${currency}` : ''}`;

export function AiActivityCard({ from, refreshToken, title, onNavigate }: { from: Date; refreshToken: number; title: (text: string) => React.ReactNode; onNavigate: (screen: Screen, param?: string, routeParams?: Record<string, any>) => void }) {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [agents, setAgents] = useState<AiAgentActivity[] | null>(null);
  const fromKey = from.toISOString();

  useEffect(() => {
    let alive = true;
    apiService.getAiAgentActivity(fromKey).then((res) => { if (alive) setAgents(res.statusCode === 1000 && res.data ? res.data.agents : []); }).catch(() => alive && setAgents([]));
    return () => { alive = false; };
  }, [fromKey, refreshToken]);

  if (!agents?.length) return null;
  return (
    <View style={styles.section}>
      {title(t('home.aiActivity.title'))}
      {agents.map((a) => {
        const off = a.status === 'suspended' || a.paused;
        const stats: Array<[string, string | number, string?]> = [
          ['chatbubbles-outline', a.conversations, t('home.aiActivity.conversations')],
          ['send-outline', a.replies, t('home.aiActivity.replies')],
          ['bag-check-outline', a.orders, a.orders ? money(a.ordersValue, a.currency) : t('home.aiActivity.orders')],
          ['document-text-outline', a.quotations, a.quotations ? money(a.quotationsValue, a.currency) : t('home.aiActivity.quotations')],
          ['person-outline', a.handoffs, t('home.aiActivity.handoffs')],
        ];
        return (
          <TouchableOpacity key={a.id} style={styles.card} activeOpacity={0.85} disabled={!a.flowId}
            onPress={() => a.flowId && onNavigate('dashboard-flows-all', undefined, { flowId: a.flowId })}>
            <View style={[styles.head, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.avatar, off && { backgroundColor: withAlpha(colors.text.secondary as string, 0.15) }]}>
                <AiBotIcon size={14} color={off ? (colors.text.secondary as string) : (colors.primary as string)} />
              </View>
              <Text style={[styles.name, { textAlign: rtl.textStart }]} numberOfLines={1}>{a.name}</Text>
              {off && <Text style={styles.off}>{a.paused ? t('aiAgents.status.paused') : t('aiAgents.status.suspended')}</Text>}
              {a.live > 0 && <Text style={styles.live}>{t('home.aiActivity.live', { count: a.live })}</Text>}
              {a.errors > 0 && <Text style={styles.errors}>{t('home.aiActivity.errors', { count: a.errors })}</Text>}
            </View>
            <View style={[styles.stats, { flexDirection: rtl.rowDirection }]}>
              {stats.map(([icon, value, label]) => (
                <View key={icon} style={styles.stat}>
                  <View style={[styles.statTop, { flexDirection: rtl.rowDirection }]}>
                    <Ionicons name={icon as any} size={13} color={colors.text.secondary as string} />
                    <Text style={styles.statValue}>{value}</Text>
                  </View>
                  <Text style={styles.statLabel} numberOfLines={1}>{label}</Text>
                </View>
              ))}
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const getStyles = () => StyleSheet.create({
  section: { gap: spacing.sm, marginBottom: spacing.lg },
  card: { padding: spacing.sm, gap: spacing.sm, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  head: { alignItems: 'center', gap: spacing.xs },
  avatar: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', backgroundColor: withAlpha(colors.primary as string, 0.12) },
  name: { flex: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  off: { fontSize: 11, color: colors.warning },
  live: { fontSize: 11, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  errors: { fontSize: 11, color: colors.error },
  stats: { flexWrap: 'wrap', gap: spacing.sm },
  stat: { minWidth: 72, flexGrow: 1 },
  statTop: { alignItems: 'center', gap: 4 },
  statValue: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  statLabel: { fontSize: 11, color: colors.text.secondary },
});
